import {
  numberWithCommas,
  weiToEthBigInt,
  safeConvertToBigInt,
  MongoLong,
  isMongoLong,
} from 'src/utils/number';
import { satsToBtc } from 'src/utils/conversion';
import { BalanceDisplayProps } from './types';
import { TBlockchain } from 'src/types';

export const BalanceDisplay = ({
  balance,
  chain: blockchainType,
  price,
}: BalanceDisplayProps) => {
  const { fiat, crypto, symbol } = getBalanceDisplay(
    balance,
    blockchainType,
    price,
  );

  return (
    <div className="flex flex-wrap items-center gap-1">
      <div className="truncate text-xl font-semibold text-foreground">
        {crypto} {symbol}
      </div>
      <span className="text-muted-foreground">•</span>
      <div className="truncate text-xl font-semibold text-muted-foreground">
        {price > 0 ? `$${numberWithCommas(fiat, 2)}` : 'N/A'}
      </div>
    </div>
  );
};

const getBalanceDisplay = (
  balance: number | MongoLong | undefined,
  chain: TBlockchain,
  price: number,
) => {
  switch (chain) {
    case 'bitcoin': {
      if (balance == null) {
        return {
          fiat: 0,
          crypto: '0.00000000',
          symbol: 'BTC',
        };
      }

      // Convert MongoDB Long to number if needed, then to BTC
      const balanceNumber = isMongoLong(balance)
        ? Number(safeConvertToBigInt(balance))
        : balance;
      const btcAmount = satsToBtc(balanceNumber);

      return {
        fiat: btcAmount * price,
        crypto: btcAmount.toFixed(8),
        symbol: 'BTC',
      };
    }
    case 'ethereum':
    case 'arbitrum':
    case 'base': {
      if (balance == null) {
        return {
          fiat: 0,
          crypto: '0.00000000',
          symbol: 'ETH',
        };
      }

      // Use BigInt for precise wei to ETH conversion
      const ethAmount = weiToEthBigInt(balance);
      const ethNumber = parseFloat(ethAmount);

      return {
        fiat: ethNumber * price,
        crypto: parseFloat(ethAmount).toFixed(8),
        symbol: 'ETH',
      };
    }
    case 'tron': {
      if (balance == null) {
        return {
          fiat: 0,
          crypto: '0.000000',
          symbol: 'TRX',
        };
      }

      // Convert MongoDB Long to number if needed
      const balanceNumber = isMongoLong(balance)
        ? Number(safeConvertToBigInt(balance))
        : balance;
      const trxAmount = balanceNumber / 1e6;

      return {
        fiat: trxAmount * price,
        crypto: trxAmount.toFixed(6),
        symbol: 'TRX',
      };
    }
    default:
      return {
        fiat: 0,
        crypto: '0',
        symbol: '',
      };
  }
};
